import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Package, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cartAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { addToLocalCart, notifyCartChanged } from '../lib/localCart';
import { productImage, resolveSrc } from '../lib/productImage';
import ProgressiveImage from './ProgressiveImage';
import { CURRENT_SEASON } from '../data/season';

export default function ProductCard({ product, index = 0, ribbon: ribbonProp = null, priority = false }) {
    // El producto de la temporada vigente (hoy el papel navideño) lleva su
    // sello, salvo que la página ya le haya puesto uno (p. ej. mejor precio).
    const ribbon = ribbonProp || (CURRENT_SEASON?.productMatch?.test(product.name) ? 'De temporada' : null);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [pickingSize, setPickingSize] = useState(false);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);
    const { src: imgSrc } = productImage(product.image_url);
    const hasVariants = product.gallery_images?.length > 1;
    const isSnacks = product.category === 'papel_snacks';
    // Único producto que no es papel por hoja: se vende por kilo, no por paca,
    // así que "Gramaje"/"Paquete" (pensados para papel) no aplican aquí.
    const isBolsas = product.category === 'bolsas_poliseda';

    // Con una sola medida se agrega de una; con varias hay que preguntar.
    // Tomar sizes[0] en silencio mandaría una medida que el cliente nunca
    // eligió — en papel, la medida ES el producto.
    const sizes = product.sizes || [];
    const needsSizeChoice = sizes.length > 1;

    const addToCart = async (size) => {
        setAdding(true);
        try {
            if (user) {
                await cartAPI.add({ product_id: product.id, quantity: 1, selected_size: size });
                notifyCartChanged();
            } else {
                addToLocalCart(product.id, 1, size);
            }
            setPickingSize(false);
            setAdded(true);
            toast.success(`${product.name} agregado al carrito`, {
                action: { label: 'Ver carrito', onClick: () => navigate('/carrito') },
            });
            setTimeout(() => setAdded(false), 2000);
        } catch {
            toast.error('No pudimos agregarlo al carrito. Intenta de nuevo.');
        }
        setAdding(false);
    };

    return (
        <article
            data-testid={`product-card-${product.id}`}
            /* `card-elevate` (index.css) hace la transición y la sombra al
               hover — no se puede expresar con utilidades arbitrarias de
               Tailwind sin que las compile mal; ver el comentario allá. */
            className={`group card-elevate relative flex flex-col rounded-sm overflow-hidden border border-[var(--line)] hover:border-[var(--kraft)] animate-fadeInUp stagger-${index + 1}`}
            style={{ opacity: 0, backgroundColor: 'var(--paper-2)' }}
        >
            {/* Doblez de esquina: al pasar el cursor, la esquina de la hoja se
                levanta y deja ver el kraft que hay debajo. Es el gesto táctil
                del sitio — la tarjeta se comporta como el material que vende.
                Decorativo puro: sin datos escondidos detrás (la ficha técnica
                ya está visible abajo) y con pointer-events desactivado. */}
            <span className="dog-ear" aria-hidden="true" />

            {ribbon && (
                // Sello de tinta, no una cinta diagonal de e-commerce: mismo
                // trazo y color que el resto de sellos del sitio, ligeramente
                // girado como si se hubiera estampado sobre el pliego.
                <span
                    className="product-stamp font-mono"
                    data-testid="product-ribbon"
                >
                    {ribbon}
                </span>
            )}

            {/* La tarjeta entera sigue llevando a la ficha, pero ya no PUEDE ser
                un <a> que envuelva todo: dentro hay botones, y un botón dentro
                de un enlace es HTML inválido y un lío con teclado y lectores de
                pantalla. El enlace se estira por encima de la tarjeta y los
                controles reales se montan sobre él con z mayor. */}
            <Link
                to={`/producto/${product.id}`}
                aria-label={`Ver ficha de ${product.name}`}
                className="absolute inset-0 z-10"
            />
            {/* El lienzo de la foto usa el tono papel: las imágenes tratadas se
                funden con él y el producto flota. `object-contain` porque
                `cover` recortaba el pliego — en una ficha de producto el
                encuadre completo importa más que llenar la caja. */}
            {imgSrc ? (
                // view-transition-name: mismo nombre que ProductDetail usa para
                // la foto de este producto — al navegar a la ficha, el navegador
                // hace un morph directo entre esta miniatura y la foto grande en
                // vez de un cross-fade genérico. Sin soporte, la propiedad se
                // ignora y no cambia nada.
                <div
                    className="aspect-[4/3] overflow-hidden"
                    style={{ backgroundColor: 'var(--paper)', viewTransitionName: `product-photo-${product.id}` }}
                >
                    <ProgressiveImage
                        src={imgSrc}
                        alt={product.name}
                        imgClassName="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                        /* `priority` lo decide la PÁGINA, no el índice.
                           Medido en /catalogo: su elemento LCP es la foto de
                           la primera tarjeta, y con `loading="lazy"` el
                           navegador no la pide hasta calcular el layout —
                           Lighthouse lo cobraba como 6.4 s de LCP en una
                           página que por lo demás pinta en 1.1 s. `lazy` es
                           correcto fuera de pantalla; sobre el pliegue es un
                           antipatrón.
                           Pero "las tres primeras" NO significa lo mismo en
                           cada página: en la portada esta rejilla va debajo
                           del hero, y marcarlas eager ahí le robaría ancho de
                           banda a la imagen que sí decide el LCP. Por eso lo
                           dice quien conoce el contexto: hoy solo /catalogo,
                           donde la rejilla es lo primero que se ve. */
                        /* Rejilla de 1/2/3 columnas (Catalog.js), dentro de
                           max-w-7xl con márgenes. Sin esto se bajaba la foto
                           de 1600 px para una tarjeta de ~340 px en celular. */
                        sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
                        priority={priority}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchpriority={priority && index === 0 ? 'high' : undefined}
                    />
                </div>
            ) : (
                <div
                    className="aspect-[4/3] flex items-center justify-center"
                    style={{ backgroundColor: 'var(--paper)' }}
                >
                    <Package className="w-12 h-12" style={{ color: 'var(--kraft)' }} />
                </div>
            )}

            {hasVariants && (
                <div className="flex items-center gap-2 px-5 pt-3">
                    {product.gallery_images.slice(0, 5).map((img, i) => (
                        <span
                            key={i}
                            className="w-7 h-7 rounded-full overflow-hidden ring-1"
                            style={{ '--tw-ring-color': 'var(--line)' }}
                        >
                            <img src={resolveSrc(img)} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" />
                        </span>
                    ))}
                    <span className="font-mono text-[10px] ml-1" style={{ color: 'var(--ink-soft)' }}>
                        {product.gallery_images.length} diseños
                    </span>
                </div>
            )}

            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <h3
                        className="font-heading text-lg font-bold leading-tight transition-colors duration-200 group-hover:text-[var(--kraft-deep)]"
                        style={{ color: 'var(--ink)' }}
                    >
                        {product.name}
                    </h3>
                    <ArrowRight
                        className="w-4 h-4 flex-shrink-0 mt-1 transition-transform duration-300 group-hover:translate-x-1"
                        style={{ color: 'var(--kraft-deep)' }}
                    />
                </div>

                {/* Ficha técnica en mono: metadato, no prosa */}
                <dl className="grid grid-cols-3 gap-2 mb-5">
                    {[
                        isBolsas
                            ? ['Presentaciones', product.sizes?.length || '—']
                            : isSnacks
                                ? ['Diseños', product.gallery_images?.length || 1]
                                : product.weight_grams > 0
                                    ? ['Gramaje', `${product.weight_grams}g`]
                                    : ['Presentaciones', product.sizes?.length || '—'],
                        ['Medida', product.sizes?.[0] || '—'],
                        isBolsas
                            ? ['Precio', 'por kg']
                            : isSnacks
                                ? ['Unidad', '1 millar']
                                : ['Paquete', `${product.package_weight_kg}kg`],
                    ].map(([label, value]) => (
                        <div key={label} className="flex flex-col gap-1">
                            <dt
                                className="font-mono text-[9px] uppercase tracking-[0.14em]"
                                style={{ color: 'var(--ink-soft)' }}
                            >
                                {label}
                            </dt>
                            <dd className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
                                {value}
                            </dd>
                        </div>
                    ))}
                </dl>

                <div
                    className="flex items-end justify-between gap-3 pt-3 mt-auto border-t"
                    style={{ borderColor: 'var(--line)' }}
                >
                    <p>
                        <span className="font-heading text-2xl font-black" style={{ color: 'var(--ink)' }}>
                            ${product.price}
                        </span>
                        <span className="font-mono text-[10px] ml-1.5" style={{ color: 'var(--ink-soft)' }}>
                            MXN{isBolsas ? '/kg' : ''}
                        </span>
                    </p>
                    {(isSnacks || product.sizes?.length > 1) && (
                        <span
                            className="font-mono text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-sm border whitespace-nowrap"
                            style={{ color: 'var(--kraft-deep)', borderColor: 'var(--line)' }}
                        >
                            {isSnacks ? 'Antigrasa' : `${product.sizes.length} medidas`}
                        </span>
                    )}
                </div>
            </div>

            {/* Barra de compra: el canto inferior del pliego, no un botón
                pegado encima. Siempre visible y nunca al hover — en móvil no
                existe el hover, y buena parte de estos pedidos se arman desde
                el teléfono, en el mostrador del negocio.

                Las dos caras de la barra miden EXACTAMENTE lo mismo (h-12): si
                el selector de medidas fuera más alto que el botón, abrirlo
                estiraría toda la fila de la rejilla y el catálogo entero daría
                un salto por un clic en una sola tarjeta. */}
            {pickingSize ? (
                <div
                    role="group"
                    aria-label={`Elige la medida de ${product.name}`}
                    data-testid={`quick-add-sizes-${product.id}`}
                    className="relative z-20 flex h-12 items-center gap-1.5 overflow-x-auto border-t px-3 animate-slideDown"
                    style={{ borderColor: 'var(--line)' }}
                >
                    <span
                        className="font-mono text-[9px] uppercase tracking-[0.14em] flex-shrink-0"
                        style={{ color: 'var(--ink-soft)' }}
                    >
                        Medida
                    </span>
                    {sizes.map((size) => (
                        <button
                            key={size}
                            type="button"
                            data-testid={`quick-add-size-${product.id}`}
                            onClick={() => addToCart(size)}
                            disabled={adding}
                            className="h-9 flex-shrink-0 whitespace-nowrap rounded-sm border px-3 text-xs font-bold border-[var(--line)] text-[var(--ink)] hover:border-[var(--kraft-deep)] hover:bg-[var(--kraft)] disabled:opacity-40 transition-colors duration-200"
                        >
                            {size}
                        </button>
                    ))}
                </div>
            ) : (
                <button
                    type="button"
                    data-testid={`quick-add-${product.id}`}
                    onClick={() => (needsSizeChoice ? setPickingSize(true) : addToCart(sizes[0] || ''))}
                    disabled={adding}
                    className="relative z-20 flex h-12 w-full items-center justify-center gap-2 border-t font-mono text-[11px] uppercase tracking-[0.16em] border-[var(--line)] text-[var(--kraft-deep)] hover:bg-[var(--kraft)] hover:text-[var(--ink)] disabled:opacity-50 transition-colors duration-200"
                >
                    {added ? (
                        <><Check className="w-3.5 h-3.5" /> Agregado</>
                    ) : (
                        <><Plus className="w-3.5 h-3.5" /> {needsSizeChoice ? 'Elegir medida' : 'Agregar'}</>
                    )}
                </button>
            )}
        </article>
    );
}
